import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { Customer, Address } from '../../types';
import * as customerService from '../../services/customer-service';

interface CustomerState {
  items: Customer[];
  selectedCustomer: Customer | null;
  addresses: Address[];
  loading: boolean;
  error: string | null;
}

const initialState: CustomerState = {
  items: [],
  selectedCustomer: null,
  addresses: [],
  loading: false,
  error: null,
};

export const searchCustomers = createAsyncThunk(
  'customers/searchCustomers',
  async (searchTerm: string, { rejectWithValue }) => {
    try {
      const response = await customerService.searchCustomers(searchTerm);
      if (!response.success) {
        throw new Error('Failed to search customers');
      }
      return response.data;
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

export const fetchCustomers = createAsyncThunk(
  'customers/fetchCustomers',
  async (_, { rejectWithValue }) => {
    try {
      const response = await customerService.getCustomers();

      // Check if response is an array (direct data from API)
      if (Array.isArray(response)) {
        return response;
      }

      // Handle structured response with success/message/data fields
      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch customers');
      }
      return response.data;
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

export const fetchCustomerAddresses = createAsyncThunk(
  'customers/fetchAddresses',
  async (customerId: string, { rejectWithValue }) => {
    try {
      const response = await customerService.getAddresses(customerId);
      if (!response.success) {
        throw new Error(response.message || 'Failed to fetch customer addresses');
      }
      return response.data;
    } catch (error) {
      return rejectWithValue((error as Error).message);
    }
  }
);

const customerSlice = createSlice({
  name: 'customers',
  initialState,
  reducers: {
    selectCustomer: (state, action: PayloadAction<Customer>) => {
      state.selectedCustomer = action.payload;
    },
    clearSelectedCustomer: state => {
      state.selectedCustomer = null;
      state.addresses = [];
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchCustomers.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCustomers.fulfilled, (state, action) => {
        state.loading = false;
        state.items = action.payload;
      })
      .addCase(fetchCustomers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchCustomerAddresses.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCustomerAddresses.fulfilled, (state, action) => {
        state.loading = false;
        state.addresses = action.payload;
      })
      .addCase(fetchCustomerAddresses.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(searchCustomers.fulfilled, (state, action: PayloadAction<Customer[]>) => {
        state.items = action.payload;
        state.loading = false;
        state.error = null;
      })
      .addCase(searchCustomers.pending, state => {
        state.loading = true;
        state.error = null;
      })
      .addCase(searchCustomers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { selectCustomer, clearSelectedCustomer } = customerSlice.actions;
export default customerSlice.reducer;
